import Image from "@/components/common/ImageWithFallback";
import Link from "next/link";
import { twMerge } from "tailwind-merge";
import { useIsDark } from "@/context/ThemeContext";

export default function BlogCard({
  image,
  blurDataURL,
  author,
  authorSlug,
  authorImage,
  date,
  title,
  description,
  slug,
  tags,
  tag,
  basePath = "/blog",
  showAuthor = true,
}) {
  const isdark = useIsDark();

  const postUrl = `${basePath}/${slug}`;
  const descriptiveText = `Read more about ${title}`;

  const tagsToRender = tags || (tag ? [tag] : []);

  return (
    <div className="group bg-card rounded-lg overflow-hidden shadow-md flex flex-col border border-transparent hover:border-[#FF1CF780] hover:shadow-[0_4px_4px_#FF1CF733] transition-all duration-200 ease-in w-full h-auto mx-auto max-w-[512px]">
      {/* Image */}
      <div className="aspect-20/9 w-full max-w-[512px] h-auto max-h-[230px] overflow-hidden relative flex-shrink-0 cursor-pointer">
        <Link href={postUrl}>
          <div>
            <Image
              src={image}
              alt={title}
              fill
              sizes="(max-width: 640px) 120vw,
                 (max-width: 1024px) 60vw,
                 700px"
              quality={92}
              placeholder={blurDataURL ? "blur" : "empty"}
              blurDataURL={blurDataURL}
              fallbackSrc="/assets/blogFallback.webp"
              className="object-cover"
            />
            {/* Top-left white tags */}
            {tagsToRender.length > 0 && (
              <div
                initial={{ opacity: 1, y: 0 }}
                transition={{
                  type: "spring",
                  mass: 1,
                  stiffness: 120,
                  damping: 18,
                }}
                className="absolute top-[10px] mobile:top-[6px] sm:top-[10px] md:top-[6px] tablet:top-[8px] lg:top-[10px] left-[10px] z-20 flex flex-wrap gap-1 lg:gap-2"
              >
                {tagsToRender.map((tagItem, index) => (
                  <span
                    key={index}
                    className={[
                      "inline-block rounded-[10px] bg-white text-black text-[9px] mobile:text-[8px] sm:text-[10px] md:text-[8px] tablet:text-[9px] lg:text-[10px] xl:text-[12px]  laptop:text-[12px] 3xl:text-[13px] 4xl:text-[14px] leading-none",
                      "p-[6px_8px] mobile:p-[6px_8px] sm:p-[7px_10px] md:p-[5px_8px] lg:p-[6px_9px] xl:p-[7px_10px] laptop:p-[8px_12px] shadow-md",
                      "font-alexandria font-light",
                    ].join(" ")}
                  >
                    {tagItem}
                  </span>
                ))}
              </div>
            )}
          </div>
        </Link>
      </div>
      {/* Content */}
      <div className="p-4 flex flex-col  justify-between">
        {/* Author + Date with Avatar */}
        {showAuthor ? (
          <div className="flex items-center gap-2 sm:gap-3 text-sm text-gray-400">
            {authorSlug ? (
              <Link href={`/blog/author/${authorSlug}`}>
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden flex-shrink-0 relative cursor-pointer hover:ring-2 hover:ring-purple-500 transition-all duration-200">
                  <Image
                    src={authorImage}
                    alt={author}
                    fill
                    sizes="100px"
                    quality={70}
                    fallbackSrc="/assets/fallbacks/avatar-fallback.svg"
                    className="object-cover"
                  />
                </div>
              </Link>
            ) : (
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden flex-shrink-0 relative">
                <Image
                  src={authorImage}
                  alt={author}
                  fill
                  sizes="100px"
                  quality={70}
                  fallbackSrc="/assets/fallbacks/avatar-fallback.svg"
                  className="object-cover"
                />
              </div>
            )}

            <div>
              {authorSlug ? (
                <Link href={`/blog/author/${authorSlug}`}>
                  <p className="font-manrope font-medium text-[12px] sm:text-[14px] laptop:text-[14px] 3xl:text-[18px] leading-[18px] sm:leading-[21px] tracking-[0.15px] text-foreground truncate cursor-pointer hover:underline transition-all duration-200">
                    {author}
                  </p>
                </Link>
              ) : (
                <p className="font-manrope font-medium text-[12px] sm:text-[14px] laptop:text-[14px] 3xl:text-[18px] leading-[18px] sm:leading-[21px] tracking-[0.15px] text-foreground truncate">
                  {author}
                </p>
              )}

              <p className="font-manrope font-medium text-[11px] sm:text-[12px] laptop:text-[12px] 3xl:text-[16px] leading-[18px] sm:leading-[21px] tracking-[0.15px] dark:text-[#909090] text-[#5A5A6E]">
                {date}
              </p>
            </div>
          </div>
        ) : (
          date && (
            <div className="flex items-center gap-2 sm:gap-3 text-sm text-gray-400 mb-2">
              <p className="font-manrope font-medium text-[11px] sm:text-[12px] laptop:text-[12px] 3xl:text-[16px] leading-[18px] sm:leading-[21px] tracking-[0.15px] dark:text-[#909090] text-[#5A5A6E]">
                {date}
              </p>
            </div>
          )
        )}

        <p>
          <Link
            href={postUrl}
            title={title}
            className={twMerge(
              "font-alexandria font-normal text-[18px] mobile:text-[16px] sm:text-[19px] lg:text-[20px] laptop:text-[20px] 3xl:text-[25px] mobile:leading-[27px] sm:leading-[28px] md:leading-[30px] tablet:leading-[36px] leading-[36px] tracking-[0.15px] mt-2 bg-clip-text text-transparent line-clamp-2 transition-all duration-300 cursor-pointer",
              isdark
                ? "bg-[linear-gradient(94.97deg,#FF1CF7_26.31%,#EEEEFF_110.92%)]"
                : "bg-[linear-gradient(94.97deg,#F81BF1_26.31%,#1A1A1A_110.92%)]",
            )}
          >
            {title}
          </Link>
        </p>

        <p
          title={description}
          className="font-manrope font-normal text-[14px] laptop:text-[14px] 3xl:text-[18px] leading-6 tracking-[0.15px] dark:text-[#909090] text-[#5A5A6E] mt-2 flex-grow line-clamp-3 tablet:line-clamp-4 lg:leading-relaxed transition-all duration-300"
        >
          {description}
        </p>

        <Link
          href={postUrl}
          aria-label={descriptiveText}
          className="font-alexandria font-normal text-[16px] lg:text-[16px] laptop:text-[16px] 3xl:text-[20px] leading-[21px] tracking-[0.15px] text-foreground mt-4 lg:mb-4 flex items-center gap-2 hover:underline"
        >
          Read more{" "}
          <Image
            src={
              isdark
                ? "/assets/blog/arrowdark.svg"
                : "/assets/blog/arrowlight.svg"
            }
            alt="arrow"
            width={18}
            height={18}
            className="inline-block"
          />
        </Link>
      </div>
    </div>
  );
}
